package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.compose.css.JustifyContent
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

val LEmptyStateStyle = CssStyle.base {
    Modifier
        .position(Position.Relative)
        .overflow(Overflow.Hidden)
        .backgroundColor(colorMode.toSitePalette().surface)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Card)
        .padding(topBottom = Space.Huge, leftRight = Space.XXL)
}

val LEmptyStateContentStyle = CssStyle.base {
    Modifier
        .position(Position.Relative)
        .zIndex(1)
        .maxWidth(32.cssRem)
        .textAlign(TextAlign.Center)
}

enum class LEmptyStateIcon {
    LANGUAGE,
    SUCCESS,
    REFRESH,
    CODE,
    FILES,
    KEY,
    SEARCH,
    AUTOMATION,
    INSIGHTS,
    INBOX,
    PROPOSAL,
    AUDIT,
    WEBHOOK,
    FORBIDDEN,
    HELP,
}

@Composable
private fun EmptyStateIcon(icon: LEmptyStateIcon) {
    val modifier = Modifier.iconSize(IconSize.Display)
    when (icon) {
        LEmptyStateIcon.LANGUAGE -> LLanguagesIcon(modifier)
        LEmptyStateIcon.SUCCESS -> LCircleCheckIcon(modifier)
        LEmptyStateIcon.REFRESH -> LRefreshIcon(modifier)
        LEmptyStateIcon.CODE -> LBracesIcon(modifier)
        LEmptyStateIcon.FILES -> LFilesIcon(modifier)
        LEmptyStateIcon.KEY -> LKeyIcon(modifier)
        LEmptyStateIcon.SEARCH -> LSearchXIcon(modifier)
        LEmptyStateIcon.AUTOMATION -> LBotIcon(modifier)
        LEmptyStateIcon.INSIGHTS -> LChartIcon(modifier)
        LEmptyStateIcon.INBOX -> LMailIcon(modifier)
        LEmptyStateIcon.PROPOSAL -> LSparkleIcon(modifier)
        LEmptyStateIcon.AUDIT -> LHistoryIcon(modifier)
        LEmptyStateIcon.WEBHOOK -> LExternalLinkIcon(modifier)
        LEmptyStateIcon.FORBIDDEN -> LDangerIcon(modifier)
        LEmptyStateIcon.HELP -> LHelpIcon(modifier)
    }
}

/**
 * The screen a surface draws when it has nothing to draw, and the one thing to do about it.
 *
 * **[action] is nullable and [secondaryAction] comes before it in the signature, and both of those
 * are call-site decisions rather than taste.** Thirty of the seventy-three call sites pass `{}` —
 * a slot they were forced to fill and had nothing to put in — and the plan is for each of them to
 * gain a real invitation. Until they do, `{}` must keep rendering what it renders today: nothing,
 * and no wrapper around the nothing. An always-emitted actions row would take the column's
 * `Space.LG` gap on every one of those thirty screens, which is a sixteen-pixel hole under the
 * sentence with no control in it.
 *
 * So the row exists only when there are two controls to lay out side by side. With one, the action
 * is a child of the column exactly as it has always been. And [action] stays last so that every
 * existing `LEmptyState(…) { LButton(…) }` keeps binding its trailing lambda to the primary action
 * rather than silently becoming the secondary one — which would compile, render, and be wrong.
 *
 * [secondaryAction] is for the second-best next step, not a second first step: "Import a file" beside
 * "Add a key", "Read the guide" beside "Connect a provider". An empty state invites one action; the
 * second is there for the reader whose answer to the first is no.
 */
@Composable
fun LEmptyState(
    title: String,
    description: String,
    modifier: Modifier = Modifier,
    icon: LEmptyStateIcon = LEmptyStateIcon.LANGUAGE,
    secondaryAction: (@Composable () -> Unit)? = null,
    action: (@Composable () -> Unit)? = null,
) {
    Box(
        // An empty state is the answer to a question the reader just asked — a filter that matched
        // nothing, a queue that is clear. The same 6px rise the cards it replaces would have used,
        // so the surface resolves rather than blinking between two different answers.
        LEmptyStateStyle.toModifier().enterRiseUnlessOwned().then(modifier),
        contentAlignment = Alignment.Center,
    ) {
        // Centred copy needs a centred hole in the mask: the default fade is anchored right of
        // centre for a left-aligned marketing column, which puts its densest glyphs across the
        // right half of this card's paragraph.
        GlyphMosaic(focus = GlyphMosaicFocus.CENTER)
        Column(
            LEmptyStateContentStyle.toModifier().gap(Space.LG),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Div(
                Modifier
                    .color(com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toSitePalette().accent)
                    .margin(0.px)
                    .toAttrs {
                        attr("aria-hidden", "true")
                    },
            ) {
                EmptyStateIcon(icon)
            }
            H3(
                Modifier
                    .displayFont()
                    .color(com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toSitePalette().textPrimary)
                    .fontSize(TypeScale.HeadingSmall)
                    .fontWeight(700)
                    .lineHeight(TypeScale.DisplayLineHeight)
                    .margin(0.px)
                    .toAttrs(),
            ) {
                Text(title)
            }
            P(
                Modifier
                    .color(com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    .lineHeight(TypeScale.BodyLineHeight)
                    .margin(0.px)
                    .toAttrs(),
            ) {
                Text(description)
            }
            if (secondaryAction != null) {
                Row(
                    Modifier
                        .gap(Space.SM)
                        .flexWrap(FlexWrap.Wrap)
                        .justifyContent(JustifyContent.Center),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    action?.invoke()
                    secondaryAction()
                }
            } else {
                action?.invoke()
            }
        }
    }
}
